import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { LoginSchema } from '@redpandev/shared';
import { Button, Field, Input, useReducedMotion, type MascotMood } from '@redpandev/ui';
import { useAuth } from '../auth/AuthContext';
import { safeReturnTo, withReturnTo } from '../auth/returnTo';
import { ApiError } from '../lib/api';
import { AuthCard, authLink } from './auth/AuthCard';
import { FormAlert } from './auth/FormAlert';
import { SocialAuthButtons } from './auth/SocialAuthButtons';

const MESSAGES: Record<string, string> = {
  invalid_credentials: 'Email ou mot de passe incorrect.',
  too_many_attempts: 'Trop de tentatives. Réessaie dans quelques minutes.',
  account_banned: 'Ce compte est suspendu.',
  // Erreurs du callback social (spec 11 §4.2) — redirigé en `?error=<code>`.
  oauth_state_invalid: 'La connexion a expiré. Réessaie.',
  oauth_exchange_failed: 'La connexion avec le fournisseur a échoué. Réessaie.',
  provider_email_missing:
    'Ce fournisseur n’a pas partagé ton adresse email. Crée un compte avec ton email, puis relie-le.',
  provider_email_unverified: 'Ton adresse n’est pas encore vérifiée chez ce fournisseur.',
  identity_already_linked: 'Ce compte social est déjà lié à un autre profil.',
  password_not_set:
    'Un compte existe déjà avec cet email. Connecte-toi par ton autre moyen, puis relie ce compte depuis Réglages → Sécurité.',
};

/** Petit temps de fête (mascotte celebrate) avant la redirection. */
const CELEBRATE_DELAY_MS = 600;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // `?returnTo=` VALIDÉ (anti open-redirect) : funnel invité du Motus du jour
  // (spec games/motus-daily §9.1) — prioritaire sur le `state.from` de RequireAuth.
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const reduced = useReducedMotion();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  // Erreur d'un retour de callback social (`/login?error=<code>`) — affichée d'emblée.
  const [formError, setFormError] = useState<string | undefined>(() => {
    const code = searchParams.get('error');
    return code ? (MESSAGES[code] ?? 'La connexion a échoué. Réessaie.') : undefined;
  });
  const [submitting, setSubmitting] = useState(false);
  // États d'humeur de la mascotte (purement décoratifs, feedback texte à côté).
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [editedSinceError, setEditedSinceError] = useState(false);
  const [shakeNonce, setShakeNonce] = useState(0);

  const mood: MascotMood = celebrating
    ? 'celebrate'
    : passwordFocused
      ? 'shy'
      : formError && !editedSinceError
        ? 'sad'
        : 'happy';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(undefined);
    setEditedSinceError(false);
    const parsed = LoginSchema.safeParse({ email, password });
    if (!parsed.success) {
      const f = parsed.error.flatten().fieldErrors;
      setErrors({ email: f.email?.[0], password: f.password?.[0] });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await login(parsed.data);
      setCelebrating(true);
      if (!reduced) await new Promise((r) => setTimeout(r, CELEBRATE_DELAY_MS));
      const from = (location.state as { from?: string } | null)?.from;
      navigate(returnTo ?? from ?? '/profile', { replace: true });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'error';
      setFormError(MESSAGES[code] ?? 'Une erreur est survenue. Réessaie.');
      setShakeNonce((n) => n + 1);
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Re-bonjour !"
      mood={mood}
      shakeNonce={shakeNonce}
      footer={
        <>
          Pas de compte ? {/* Le returnTo suit le joueur entre login et signup (funnel invité). */}
          <Link to={returnTo ? withReturnTo('/signup', returnTo) : '/signup'} className={authLink}>
            Rejoins la meute !
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormAlert>{formError}</FormAlert>
        <Field label="Email" error={errors.email}>
          <Input
            name="email"
            type="email"
            autoComplete="email"
            placeholder="toi@exemple.fr"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEditedSinceError(true);
            }}
            required
          />
        </Field>
        <div>
          <Field label="Mot de passe" error={errors.password}>
            <Input
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setEditedSinceError(true);
              }}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              required
            />
          </Field>
          <p className="mt-1.5 text-right text-sm">
            <Link
              to="/forgot-password"
              className="rounded-sm text-ink-400 underline-offset-4 hover:underline"
            >
              Mot de passe oublié ?
            </Link>
          </p>
        </div>
        <Button type="submit" size="lg" loading={submitting} className="w-full">
          Se connecter
        </Button>
      </form>
      {/* Boutons sociaux (spec 11 §13) — rien si la feature est dark (flags off). */}
      <div className="mt-5">
        <SocialAuthButtons returnTo={returnTo} />
      </div>
    </AuthCard>
  );
}
